<script lang="ts" setup>
import {
    faArrowsRetweet,
    faCog,
    faInbox,
    faLifeRing,
    faObjectsColumn,
    faPeopleGroup,
    type IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import {
    faLeftToLine,
    faRightFromLine,
} from "@fortawesome/pro-light-svg-icons";
import { faBolt } from "@fortawesome/pro-solid-svg-icons";
import type { Link } from "#ui/types";
import type {
    RouteLocationAsRelativeGeneric,
    RouteLocationRaw,
} from "vue-router";
import { convertToShortNumber } from "~/utils";
import type { ComputedRef } from "vue";

interface NavRoute extends Link {
    label: string;
    icon: IconDefinition;
    children?: NavRoute[];
    counter?: string;
    counter_label?: string;
    external?: boolean;
    exactActive?: boolean;
    active?: boolean;
}

const { user, hasRole, hasAnyRoles } = useUser();
const collapsed = computed({
    get: () => !!user.value?.preferences?.collapsed_menu,
    set: (value: boolean) => {
        if (!user.value) return;

        user.value.preferences = {
            ...user.value.preferences,
            collapsed_menu: value,
        };

        useApi().users.update(user.value.id, {
            preferences: user.value.preferences,
        });
    },
});

type MappedRoute = NavRoute & Record<string, any>;

const routes: ComputedRef<Record<string, MappedRoute[]>> = computed(() => {
    const current = useRoute();
    const all = useRouter().getRoutes();

    let settingsRoute: RouteLocationRaw = "/";

    if (hasAnyRoles(["owner", "admin"])) {
        settingsRoute = { name: "settings-account" };
    } else if (hasRole("user")) {
        settingsRoute = { name: "settings-me-profile" };
    } else if (hasRole("billing")) {
        settingsRoute = { name: "settings-account-billing" };
    }

    const routes: Record<string, MappedRoute[]> = {
        top: [
            {
                to: "/",
                icon: faObjectsColumn,
                label: "Dashboard",
            },
            {
                to: {
                    name: "people",
                },
                icon: faPeopleGroup,
                label: "People",
                counter: "people_count",
                counter_label: "Unique people in your account",
                keep: hasAnyRoles(["owner", "admin"]),
            },
            {
                to: {
                    name: "automations",
                },
                icon: faArrowsRetweet,
                label: "Automations",
                counter: "automations_count",
                counter_label: "Active automations",
                keep: hasAnyRoles(["owner", "admin"]),
            },
            {
                to: {
                    name: "tasks",
                },
                icon: faBolt,
                label: "Task History",
                counter: "tasks_count",
                count: totalTasks.value ? totalTasks.value : undefined,
                counter_label: "Tasks executed",
                keep: hasAnyRoles(["owner", "admin"]),
            },
        ].filter((route) => route.keep === undefined || route.keep),
        bot: [
            {
                to: "https://help.hiredata.com/en/",
                icon: faLifeRing,
                label: "Support",
                external: true,
                // counter: 'tasks_count',
                // counter_label: 'Total actions executed this month.'
            },
            {
                to: settingsRoute,
                icon: faCog,
                label: "Settings",
                // counter: 'tasks_count',
                // counter_label: 'Total actions executed this month.'
            },
        ],
    };

    if (user.value?.flags?.can_show_inbox) {
        routes.top.splice(1, 0, {
            to: "/inbox",
            icon: faInbox,
            label: "Inbox",
        });
    }

    for (const key of Object.keys(routes) as (keyof typeof routes)[]) {
        routes[key] = routes[key].map((route: MappedRoute) => {
            const name =
                typeof route.to === "object"
                    ? ((route.to as RouteLocationAsRelativeGeneric).name ??
                      route.to.path)
                    : route.to;

            route.exactActive = current.name === name || current.path === name;
            route.active =
                route.exactActive ||
                !!all.find(
                    (r) =>
                        (r &&
                            r.name &&
                            (r.name as string).startsWith(name as string) &&
                            (r.name as string).startsWith(
                                current.name as string,
                            )) ||
                        (r &&
                            r.path &&
                            r.path.startsWith(name as string) &&
                            r.path.startsWith(current.name as string)),
                );

            return route;
        });
    }

    return routes;
});

const defaultCounterQuery = ref<any>(null);
const storage = useLocalStorage("pagination:/tasks:/tasks", "");
if (storage.value) {
    defaultCounterQuery.value = JSON.parse(storage.value || "");
}
const { data: stats, refresh } = useApi().live_stats.subscribe(
    "menu-counters",
    {
        query: defaultCounterQuery.value,
        immediate: false,
    },
);

const data = ref({} as Record<string, any>);

watchImmediate(stats, (to) => {
    if (to) {
        data.value = to.data;
    }
});

const { value: totalTasks, unsubscribe } = useStoredValue(
    "total-tasks",
    () => undefined,
);
const { echo } = useAppEcho();

onMounted(() => {
    echo.private(`live-stats.${user.value?.account.id}.menu-counters`).listen(
        "LiveStatUpdate",
        (update: { data: any }) => {
            data.value = update.data as typeof data.value;
        },
    );

    refresh();
});

onBeforeUnmount(() => {
    unsubscribe();
    echo.leaveChannel(`live-stats.${user.value?.account.id}.menu-counters`);
});
</script>

<template>
    <div
        :class="{
            'md:w-80': !collapsed,
            'md:w-16': collapsed,
        }"
        class="bg-hd-neutral-50 flex-shrink-0 relative hidden md:block"
    >
        <div class="flex flex-col h-full">
            <div
                :class="
                    collapsed
                        ? 'h-16 w-16 justify-center'
                        : 'justify-between py-4 h-20 px-4 '
                "
                class="flex items-center relative z-10"
            >
                <NuxtLink
                    :class="{
                        'pl-4': !collapsed,
                    }"
                    class="block relative h-8"
                    to="/"
                >
                    <AppLogo
                        :type="collapsed ? 'icon' : undefined"
                        class="h-full"
                    />
                </NuxtLink>
                <UBadge
                    v-if="user.account.is_partner"
                    :class="{
                        'translate-x-32': !collapsed,
                        'translate-x-[calc(100%+1rem)]': collapsed,
                    }"
                    :ui="{
                        rounded: 'rounded-full',
                        base: '!bg-hd-primary-900',
                    }"
                    class="transform duration-150 ease-in-out font-normal px-2 py-1 absolute"
                    color="hd-primary"
                    label="Partner"
                    size="xs"
                />
                <UButton
                    :class="{
                        'absolute opacity-0 hover:opacity-100 w-12 h-12 justify-center':
                            collapsed,
                    }"
                    color="hd-neutral"
                    size="sm"
                    square
                    variant="soft"
                    @click="collapsed = !collapsed"
                >
                    <FontAwesomeIcon
                        :icon="collapsed ? faRightFromLine : faLeftToLine"
                        :size="collapsed ? 'xl' : 'lg'"
                    />
                </UButton>
            </div>

            <ul
                :class="{
                    'gap-2': collapsed,
                }"
                class="w-full flex-1 flex py-4 flex-col"
            >
                <!-- Sidebar content here -->
                <li v-for="(route, index) in routes.top" :key="index">
                    <NuxtLink
                        :class="{
                            '!bg-hd-primary-original text-white': route.active,
                            'px-6 py-4 w-full': !collapsed,
                            'mx-auto w-12 h-12 rounded-xl justify-center':
                                collapsed,
                        }"
                        :external="route.external"
                        :target="route.external ? '_blank' : undefined"
                        :to="route.to"
                        active-class="!bg-hd-primary-original text-white font-medium"
                        class="relative transition duration-75 flex items-center ease-in-out gap-2 hover:bg-hd-primary-original/80 active:bg-hd-primary-original hover:text-white active:text-white"
                    >
                        <FontAwesomeIcon :icon="route.icon" fixed-width />
                        <span v-if="!collapsed" class="flex-1">{{
                            route.label
                        }}</span>
                        <UTooltip
                            v-if="
                                route.counter &&
                                (route.count || data[route.counter as string])
                            "
                            :class="{
                                'absolute -right-1 top-2 transform -translate-y-1/2':
                                    collapsed,
                            }"
                            :popper="{ placement: 'right' }"
                            :text="route.counter_label"
                        >
                            <UBadge
                                :class="{
                                    'text-[11px] min-w-5 justify-center':
                                        collapsed,
                                }"
                                :size="collapsed ? 'xs' : undefined"
                                color="primary"
                                >{{
                                    convertToShortNumber(
                                        route.count ??
                                            data[route.counter as string],
                                    )
                                }}
                            </UBadge>
                        </UTooltip>
                    </NuxtLink>
                </li>
            </ul>
            <div class="w-full">
                <ul
                    :class="collapsed ? '' : 'pr-4'"
                    class="w-full text-sm flex-1 flex flex-col gap-0.5"
                >
                    <!-- Sidebar content here -->
                    <li v-for="(route, index) in routes.bot" :key="index">
                        <NuxtLink
                            :class="{
                                '!bg-hd-primary-original text-white':
                                    route.active,
                                'px-6 py-3 w-full rounded-r-md': !collapsed,
                                'mx-auto w-12 h-12 rounded-xl justify-center':
                                    collapsed,
                            }"
                            :external="route.external"
                            :target="route.external ? '_blank' : undefined"
                            :to="route.to"
                            active-class="!bg-hd-primary-original text-white"
                            class="transition duration-75 flex items-center ease-in-out gap-2 hover:bg-hd-primary-original/80 active:bg-hd-primary-original hover:text-white active:text-white"
                        >
                            <FontAwesomeIcon :icon="route.icon" fixed-width />
                            <span v-if="!collapsed" class="flex-1">{{
                                route.label
                            }}</span>
                            <UTooltip
                                v-if="
                                    route.counter &&
                                    (route.count || data[route.counter])
                                "
                                :popper="{ placement: 'right' }"
                                :text="route.counter_label"
                            >
                                <UBadge color="primary"
                                    >convertToShortNumber({{
                                        route.count ?? data[route.counter]
                                    }})
                                </UBadge>
                            </UTooltip>
                        </NuxtLink>
                    </li>
                </ul>
                <div
                    :class="collapsed ? 'px-4 py-2' : 'p-4'"
                    class="flex flex-col"
                >
                    <hr
                        :class="collapsed ? 'mb-2' : 'mb-4'"
                        class="border-hd-neutral-200 300 border rounded-full"
                    />
                    <AppUserDropdown :collapsed="collapsed" />
                </div>
            </div>
        </div>
    </div>
</template>
